import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Shield, ShieldCheck, Loader2, TrendingDown, Sparkles,
  Flame, Copy, CalendarClock, MoonStar, Banknote,
} from "lucide-react";

const INSIGHT_META = {
  drain:        { Icon: Flame,         tone: "bg-red-50 border-red-100 text-red-700",           chip: "bg-red-500" },
  duplicate:    { Icon: Copy,          tone: "bg-amber-50 border-amber-100 text-amber-800",      chip: "bg-amber-500" },
  upcoming:     { Icon: CalendarClock, tone: "bg-sky-50 border-sky-100 text-sky-700",            chip: "bg-sky-500" },
  inactive:     { Icon: MoonStar,      tone: "bg-slate-50 border-slate-200 text-slate-700",      chip: "bg-slate-500" },
  yearly_waste: { Icon: TrendingDown,  tone: "bg-emerald-50 border-emerald-100 text-emerald-700",chip: "bg-emerald-500" },
};

export const DeployShieldDialog = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleOpen = async (next) => {
    setOpen(next);
    if (!next) { window.setTimeout(() => setResult(null), 200); return; }
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.post("/audit/start");
      setResult(data);
    } catch {
      setResult({
        summary: "Shield could not be deployed right now. Try again in a moment.",
        detected_drains: 0, potential_savings: 0, insights: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {}, [open]);

  const insights = result?.insights || [];
  const synced = result?.synced_from_bank || 0;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="sm:max-w-md rounded-3xl border-2 border-slate-100 p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="deploy-shield-modal"
      >
        <div className="bg-slate-900 text-white px-6 pt-7 pb-6 relative">
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-400/20 rounded-full blur-2xl" aria-hidden="true" />
          <div className="relative flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-400 text-slate-900 flex items-center justify-center ring-4 ring-amber-400/20">
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ShieldCheck className="w-6 h-6" strokeWidth={2.6} />}
            </div>
            <div className="min-w-0">
              <DialogTitle
                className="text-2xl font-black tracking-tight text-white"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {loading ? "Deploying Shield…" : "Shield Deployed!"}
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-sm font-medium mt-0.5">
                {loading ? "Scanning recurring charges…" : "Here's your battle plan, hero."}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-6 text-slate-500">
              <Sparkles className="w-8 h-8 text-amber-500 animate-pulse" />
              <p className="mt-3 text-sm font-semibold">Scanning monthly battles…</p>
            </div>
          ) : (
            <>
              <p className="text-base font-semibold text-slate-700 leading-relaxed" data-testid="audit-summary">
                {result?.summary || "Your hero shield is ready."}
              </p>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="rounded-2xl bg-slate-50 border-2 border-slate-100 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Drains found</div>
                  <div
                    className="text-2xl font-black text-slate-900 mt-1"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                    data-testid="audit-drains"
                  >
                    {result?.detected_drains ?? 0}
                  </div>
                  {synced > 0 && (
                    <div className="text-[10px] font-bold text-slate-500 mt-1 inline-flex items-center gap-1">
                      <Banknote className="w-3 h-3" /> {synced} from bank
                    </div>
                  )}
                </div>
                <div className="rounded-2xl bg-amber-50 border-2 border-amber-100 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" /> Potential save
                  </div>
                  <div
                    className="text-2xl font-black text-amber-700 mt-1"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                    data-testid="audit-savings"
                  >
                    ${(result?.potential_savings ?? 0).toFixed(2)}
                  </div>
                  <div className="text-[10px] font-bold text-amber-700 mt-1">
                    ≈ ${((result?.potential_savings ?? 0) * 12).toFixed(0)}/yr
                  </div>
                </div>
              </div>

              {insights.length > 0 && (
                <div className="mt-6">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    Hero insights
                  </p>
                  <ul className="flex flex-col gap-2.5" data-testid="audit-insights">
                    {insights.map((ins, idx) => {
                      const meta = INSIGHT_META[ins.kind] || INSIGHT_META.inactive;
                      const Icon = meta.Icon;
                      return (
                        <li
                          key={`${ins.kind}-${idx}`}
                          className={`rounded-2xl border-2 p-3 ${meta.tone}`}
                          data-testid={`audit-insight-${ins.kind}`}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className={`w-8 h-8 rounded-xl ${meta.chip} text-white flex items-center justify-center flex-shrink-0`}>
                              <Icon className="w-4 h-4" strokeWidth={2.6} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <p
                                  className="text-sm font-black text-slate-900"
                                  style={{ fontFamily: "Outfit, sans-serif" }}
                                >
                                  {ins.title}
                                </p>
                                {ins.monthly_impact > 0 && (
                                  <span className="text-xs font-black text-slate-900 whitespace-nowrap">
                                    ${ins.monthly_impact.toFixed(2)}/mo
                                  </span>
                                )}
                              </div>
                              <p className="text-[12px] leading-snug font-medium mt-0.5">
                                {ins.message}
                              </p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {insights.length === 0 && (result?.detected_drains ?? 0) === 0 && (
                <div className="mt-5 text-center text-xs text-slate-500 font-semibold">
                  No active drains detected. You're a true money hero.
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 pb-6 pt-0">
          <Button
            onClick={() => handleOpen(false)}
            className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-bold tracking-wide"
            data-testid="audit-close-button"
          >
            <Shield className="w-4 h-4 mr-2" /> Got it, hero
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeployShieldDialog;
